import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type MouseEvent as ReactMouseEvent,
    type ReactElement,
} from 'react';
import { ActionIcon, Badge, Box, Group, Paper, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { Maximize2, Minus, Plus } from 'lucide-react';
import InitialAvatar from '@/design/InitialAvatar';
import DirectoryStatusBadge from './DirectoryStatusBadge';
import type { DirectoryUserRecord } from './DirectoryApiService';

interface DirectoryOrgChartProps {
    users: DirectoryUserRecord[];
    filteredUsers: DirectoryUserRecord[];
    filtersActive: boolean;
    loading: boolean;
    onOpenUser: (user: DirectoryUserRecord) => void;
}

function nodeKey(u: DirectoryUserRecord): string {
    return `${u.connectionId}::${u.externalId}`;
}

interface Forest {
    roots: DirectoryUserRecord[];
    childrenByManager: Map<string, DirectoryUserRecord[]>;
    byKey: Map<string, DirectoryUserRecord>;
}

/**
 * Builds a connection-scoped reporting forest from `managerId`. A user is a
 * root when it has no manager, or its manager isn't in the loaded set.
 */
function buildForest(users: DirectoryUserRecord[]): Forest {
    const byKey = new Map(users.map((u) => [nodeKey(u), u] as const));
    const childrenByManager = new Map<string, DirectoryUserRecord[]>();
    const roots: DirectoryUserRecord[] = [];
    for (const u of users) {
        const managerKey = u.managerId ? `${u.connectionId}::${u.managerId}` : null;
        if (managerKey && byKey.has(managerKey)) {
            const arr = childrenByManager.get(managerKey) ?? [];
            arr.push(u);
            childrenByManager.set(managerKey, arr);
        } else {
            roots.push(u);
        }
    }
    const byName = (a: DirectoryUserRecord, b: DirectoryUserRecord): number =>
        (a.displayName || a.email).localeCompare(b.displayName || b.email);
    roots.sort(byName);
    for (const arr of childrenByManager.values()) arr.sort(byName);
    return { roots, childrenByManager, byKey };
}

const MAX_SCALE = 1;
const INITIAL_SCALE = 0.5;
const PREVIEW_WIDTH = 288;
const PREVIEW_HEIGHT = 184;
const CURSOR_OFFSET = 18;

const clampNum = (value: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, value));

const cardStyle: CSSProperties = {
    width: 196,
    padding: '12px 14px 14px',
    background: 'var(--bm-color-body)',
    border: '1px solid var(--bm-color-border)',
    borderRadius: 'var(--bm-radius)',
    boxShadow: 'var(--bm-card-shadow)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    textAlign: 'center',
    cursor: 'pointer',
};

export default function DirectoryOrgChart({ users, filteredUsers, filtersActive, loading, onOpenUser }: DirectoryOrgChartProps): ReactElement {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const { roots, childrenByManager, byKey } = useMemo(() => buildForest(users), [users]);

    const matchedKeys = useMemo(() => new Set(filteredUsers.map(nodeKey)), [filteredUsers]);

    /**
     * When filtering, keep matched nodes plus the ancestor chain up to a root so
     * each match stays visible in context; unrelated subtrees are dropped.
     */
    const keepKeys = useMemo(() => {
        if (!filtersActive) return null;
        const keep = new Set<string>();
        for (const start of filteredUsers) {
            let cur: DirectoryUserRecord | undefined = start;
            while (cur) {
                const key = nodeKey(cur);
                if (keep.has(key)) break;
                keep.add(key);
                const managerKey: string | null = cur.managerId ? `${cur.connectionId}::${cur.managerId}` : null;
                cur = managerKey ? byKey.get(managerKey) : undefined;
            }
        }
        return keep;
    }, [filtersActive, filteredUsers, byKey]);

    const visibleRoots = useMemo(
        () => (keepKeys ? roots.filter((r) => keepKeys.has(nodeKey(r))) : roots),
        [roots, keepKeys],
    );

    /** Pan / zoom canvas state. */
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [scale, setScale] = useState(1);
    const [minScale, setMinScale] = useState(0.1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [dragging, setDragging] = useState(false);
    /** Stays hidden until the first fit lands so the unscaled layer never flashes. */
    const [ready, setReady] = useState(false);

    /**
     * Live mirrors so rapid wheel/drag events read fresh values without waiting
     * for React to commit.
     */
    const scaleRef = useRef(scale);
    const txRef = useRef(tx);
    const tyRef = useRef(ty);
    const minScaleRef = useRef(minScale);
    const draggingRef = useRef(false);
    const movedRef = useRef(false);
    const suppressClickRef = useRef(false);

    const onOpenUserRef = useRef(onOpenUser);
    useEffect(() => {
        onOpenUserRef.current = onOpenUser;
    }, [onOpenUser]);

    const applyTransform = useCallback((s: number, x: number, y: number): void => {
        scaleRef.current = s;
        txRef.current = x;
        tyRef.current = y;
        setScale(s);
        setTx(x);
        setTy(y);
    }, []);

    const clampPan = useCallback((x: number, y: number, s: number): [number, number] => {
        const vp = viewportRef.current;
        const ct = contentRef.current;
        if (!vp || !ct) return [x, y];
        const vw = vp.clientWidth;
        const vh = vp.clientHeight;
        const sw = ct.scrollWidth * s;
        const sh = ct.scrollHeight * s;
        const nx = sw <= vw ? (vw - sw) / 2 : clampNum(x, vw - sw, 0);
        const ny = sh <= vh ? (vh - sh) / 2 : clampNum(y, vh - sh, 0);
        return [nx, ny];
    }, []);

    const zoomTo = useCallback(
        (nextRaw: number, anchorX: number, anchorY: number): void => {
            const cur = scaleRef.current;
            const next = clampNum(nextRaw, minScaleRef.current, MAX_SCALE);
            if (Math.abs(next - cur) < 1e-4) return;
            const contentX = (anchorX - txRef.current) / cur;
            const contentY = (anchorY - tyRef.current) / cur;
            const [nx, ny] = clampPan(anchorX - contentX * next, anchorY - contentY * next, next);
            applyTransform(next, nx, ny);
        },
        [applyTransform, clampPan],
    );

    const measureFit = useCallback((): { vw: number; vh: number; cw: number; ch: number; fit: number } | null => {
        const vp = viewportRef.current;
        const ct = contentRef.current;
        if (!vp || !ct) return null;
        const vw = vp.clientWidth;
        const vh = vp.clientHeight;
        const cw = ct.scrollWidth;
        const ch = ct.scrollHeight;
        if (!cw || !ch) return null;
        const fit = clampNum(Math.min(vw / cw, vh / ch), 0.05, MAX_SCALE);
        return { vw, vh, cw, ch, fit };
    }, []);

    const centerAt = useCallback(
        (targetScale: number, m: { vw: number; vh: number; cw: number; ch: number; fit: number }): void => {
            /**
             * Floor zoom-out at whichever is smaller — the initial scale or the
             * fit-everything scale — so even very large charts stay reachable.
             */
            const min = Math.min(m.fit, INITIAL_SCALE);
            minScaleRef.current = min;
            setMinScale(min);
            const s = clampNum(targetScale, min, MAX_SCALE);
            const [nx, ny] = clampPan((m.vw - m.cw * s) / 2, (m.vh - m.ch * s) / 2, s);
            applyTransform(s, nx, ny);
            setReady(true);
        },
        [applyTransform, clampPan],
    );

    /** Initial / refit view: 50%, centered. */
    const resetView = useCallback((): void => {
        const m = measureFit();
        if (m) centerAt(INITIAL_SCALE, m);
    }, [measureFit, centerAt]);

    const fitToView = useCallback((): void => {
        const m = measureFit();
        if (m) centerAt(m.fit, m);
    }, [measureFit, centerAt]);

    const zoomFromButton = useCallback(
        (factor: number): void => {
            const vp = viewportRef.current;
            if (!vp) return;
            zoomTo(scaleRef.current * factor, vp.clientWidth / 2, vp.clientHeight / 2);
        },
        [zoomTo],
    );

    /** Refit when the rendered forest changes (load / filter), not on collapse. */
    const filteredCount = filteredUsers.length;
    useLayoutEffect(() => {
        resetView();
    }, [users, filtersActive, filteredCount, resetView]);

    /** Wheel-to-zoom toward the cursor (needs a non-passive listener to preventDefault). */
    useEffect(() => {
        const vp = viewportRef.current;
        if (!vp) return;
        const onWheel = (e: WheelEvent): void => {
            e.preventDefault();
            const rect = vp.getBoundingClientRect();
            zoomTo(scaleRef.current * Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top);
        };
        vp.addEventListener('wheel', onWheel, { passive: false });
        return () => vp.removeEventListener('wheel', onWheel);
    }, [zoomTo]);

    /** Keep content inside the viewport when it resizes (responsive / collapse). */
    const hasContent = visibleRoots.length > 0;
    useEffect(() => {
        const vp = viewportRef.current;
        const ct = contentRef.current;
        if (!vp || !ct) return;
        const reclamp = (): void => {
            const [nx, ny] = clampPan(txRef.current, tyRef.current, scaleRef.current);
            applyTransform(scaleRef.current, nx, ny);
        };
        const ro = new ResizeObserver(reclamp);
        ro.observe(vp);
        ro.observe(ct);
        return () => ro.disconnect();
    }, [hasContent, clampPan, applyTransform]);

    const beginPan = useCallback(
        (e: ReactMouseEvent): void => {
            if (e.button !== 0) return;
            draggingRef.current = true;
            movedRef.current = false;
            setDragging(true);
            const startX = e.clientX;
            const startY = e.clientY;
            const originX = txRef.current;
            const originY = tyRef.current;
            const onMove = (ev: globalThis.MouseEvent): void => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                if (!movedRef.current && Math.hypot(dx, dy) > 4) movedRef.current = true;
                const [nx, ny] = clampPan(originX + dx, originY + dy, scaleRef.current);
                applyTransform(scaleRef.current, nx, ny);
            };
            const onUp = (): void => {
                draggingRef.current = false;
                setDragging(false);
                if (movedRef.current) {
                    suppressClickRef.current = true;
                    setTimeout(() => {
                        suppressClickRef.current = false;
                    }, 0);
                }
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [applyTransform, clampPan],
    );

    /** Node interactions (stable so the tree stays memoized). */
    const [hovered, setHovered] = useState<DirectoryUserRecord | null>(null);
    const [cursor, setCursor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const hoveredRef = useRef<DirectoryUserRecord | null>(null);
    const rafRef = useRef(0);
    useEffect(() => {
        hoveredRef.current = hovered;
    }, [hovered]);
    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    const cursorFromEvent = useCallback((e: ReactMouseEvent): { x: number; y: number } => {
        const rect = viewportRef.current?.getBoundingClientRect();
        return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 };
    }, []);

    const handleOpen = useCallback((user: DirectoryUserRecord): void => {
        if (suppressClickRef.current) return;
        onOpenUserRef.current(user);
    }, []);

    const handleToggle = useCallback((key: string): void => {
        if (suppressClickRef.current) return;
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleEnter = useCallback(
        (user: DirectoryUserRecord, e: ReactMouseEvent): void => {
            if (scaleRef.current >= 0.999) return;
            setHovered(user);
            setCursor(cursorFromEvent(e));
        },
        [cursorFromEvent],
    );

    const handleLeave = useCallback((): void => setHovered(null), []);

    const onViewportMove = useCallback(
        (e: ReactMouseEvent): void => {
            if (!hoveredRef.current || draggingRef.current || scaleRef.current >= 0.999) return;
            const pos = cursorFromEvent(e);
            if (rafRef.current) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = 0;
                setCursor(pos);
            });
        },
        [cursorFromEvent],
    );

    const tree = useMemo(() => {
        const childrenToShow = (user: DirectoryUserRecord): DirectoryUserRecord[] => {
            const children = childrenByManager.get(nodeKey(user)) ?? [];
            return keepKeys ? children.filter((c) => keepKeys.has(nodeKey(c))) : children;
        };

        const renderNode = (user: DirectoryUserRecord): ReactElement => {
            const key = nodeKey(user);
            const children = childrenToShow(user);
            const reportCount = (childrenByManager.get(key) ?? []).length;
            /** Filtering force-expands so every match stays on screen in context. */
            const expanded = filtersActive ? true : !collapsed.has(key);
            const dim = filtersActive && !matchedKeys.has(key);
            const hasVisibleChildren = children.length > 0;

            return (
                <li key={key}>
                    <Box style={{ position: 'relative', opacity: dim ? 0.5 : 1 }}>
                        <UnstyledButton
                            className="bm-org-card"
                            style={cardStyle}
                            onClick={() => handleOpen(user)}
                            onMouseEnter={(e) => handleEnter(user, e)}
                            onMouseLeave={handleLeave}
                        >
                            <InitialAvatar source={user.displayName || user.email} size={40} variant="filled" />
                            <Box style={{ width: '100%', minWidth: 0 }}>
                                <Text size="sm" fw={600} truncate>
                                    {user.displayName || user.email}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                    {user.title ?? user.email}
                                </Text>
                            </Box>
                            <Group gap={6} wrap="nowrap" justify="center">
                                <DirectoryStatusBadge status={user.status} />
                                {reportCount > 0 && (
                                    <Badge size="xs" variant="light" color="gray">
                                        {reportCount} {reportCount === 1 ? 'report' : 'reports'}
                                    </Badge>
                                )}
                            </Group>
                        </UnstyledButton>

                        {hasVisibleChildren && !filtersActive && (
                            <Tooltip
                                label={expanded ? 'Collapse reports' : `Expand ${children.length} ${children.length === 1 ? 'report' : 'reports'}`}
                                withArrow
                            >
                                <ActionIcon
                                    variant="default"
                                    radius="xl"
                                    size="sm"
                                    aria-label={expanded ? 'Collapse reports' : 'Expand reports'}
                                    onClick={() => handleToggle(key)}
                                    style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translate(-50%, -3px)', zIndex: 2 }}
                                >
                                    {expanded ? <Minus size={14} /> : <Plus size={14} />}
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Box>

                    {hasVisibleChildren && expanded && <ul>{children.map(renderNode)}</ul>}
                </li>
            );
        };

        return <ul>{visibleRoots.map(renderNode)}</ul>;
    }, [visibleRoots, childrenByManager, keepKeys, collapsed, filtersActive, matchedKeys, handleOpen, handleToggle, handleEnter, handleLeave]);

    const hoveredReports = hovered ? (childrenByManager.get(nodeKey(hovered)) ?? []).length : 0;
    const showPreview = !!hovered && !dragging && scale < 0.999;

    let previewX = cursor.x + CURSOR_OFFSET;
    let previewY = cursor.y + CURSOR_OFFSET;
    const vw = viewportRef.current?.clientWidth ?? 0;
    const vh = viewportRef.current?.clientHeight ?? 0;
    if (previewX + PREVIEW_WIDTH > vw) previewX = cursor.x - PREVIEW_WIDTH - CURSOR_OFFSET;
    if (previewY + PREVIEW_HEIGHT > vh) previewY = cursor.y - PREVIEW_HEIGHT - CURSOR_OFFSET;
    previewX = Math.max(8, previewX);
    previewY = Math.max(8, previewY);

    return (
        <Box
            ref={viewportRef}
            onMouseDown={beginPan}
            onMouseMove={onViewportMove}
            onDragStart={(e) => e.preventDefault()}
            style={{
                position: 'relative',
                flex: 1,
                minHeight: 'min(70dvh, 560px)',
                overflow: 'hidden',
                border: '1px solid var(--bm-color-border-subtle)',
                borderRadius: 'var(--bm-radius)',
                background: 'var(--bm-color-surface-alt)',
                cursor: hasContent ? (dragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
            }}
        >
            {!hasContent ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                    {loading
                        ? 'Loading…'
                        : filtersActive
                          ? 'No users match the current filters.'
                          : 'No directory users yet — run a sync to populate.'}
                </Text>
            ) : (
                <>
                    <div
                        ref={contentRef}
                        className="bm-org"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transformOrigin: '0 0',
                            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                            transition: dragging ? 'none' : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease',
                            opacity: ready ? 1 : 0,
                            willChange: 'transform',
                        }}
                    >
                        {tree}
                    </div>

                    <Paper
                        withBorder
                        radius="md"
                        p={4}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', alignItems: 'center', gap: 2, background: 'var(--bm-color-body)' }}
                    >
                        <Tooltip label="Zoom out" withArrow>
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Zoom out" disabled={scale <= minScale + 1e-4} onClick={() => zoomFromButton(1 / 1.3)}>
                                <Minus size={15} />
                            </ActionIcon>
                        </Tooltip>
                        <Text size="xs" fw={600} c="dimmed" style={{ width: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(scale * 100)}%
                        </Text>
                        <Tooltip label="Zoom in" withArrow>
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Zoom in" disabled={scale >= MAX_SCALE - 1e-4} onClick={() => zoomFromButton(1.3)}>
                                <Plus size={15} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Fit to view" withArrow>
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Fit to view" onClick={() => fitToView()}>
                                <Maximize2 size={14} />
                            </ActionIcon>
                        </Tooltip>
                    </Paper>

                    {showPreview && hovered && (
                        <div style={{ position: 'absolute', left: previewX, top: previewY, width: PREVIEW_WIDTH, pointerEvents: 'none', zIndex: 5 }}>
                            <Paper withBorder shadow="md" radius="md" p="sm" className="bm-org-preview" style={{ background: 'var(--bm-color-body)' }}>
                                <Group gap="sm" wrap="nowrap" mb={8}>
                                    <InitialAvatar source={hovered.displayName || hovered.email} size={44} variant="filled" />
                                    <Box style={{ minWidth: 0 }}>
                                        <Text size="sm" fw={700} truncate>
                                            {hovered.displayName || hovered.email}
                                        </Text>
                                        <Text size="xs" c="dimmed" truncate>
                                            {hovered.title ?? '—'}
                                        </Text>
                                    </Box>
                                </Group>
                                <Group gap={6} mb={6}>
                                    <DirectoryStatusBadge status={hovered.status} />
                                    {hoveredReports > 0 && (
                                        <Badge size="xs" variant="light" color="gray">
                                            {hoveredReports} {hoveredReports === 1 ? 'report' : 'reports'}
                                        </Badge>
                                    )}
                                </Group>
                                <Stack gap={2}>
                                    <Text size="xs" c="dimmed" truncate>
                                        {hovered.email}
                                    </Text>
                                    {hovered.department && (
                                        <Text size="xs" truncate>
                                            {hovered.department}
                                        </Text>
                                    )}
                                </Stack>
                            </Paper>
                        </div>
                    )}
                </>
            )}
        </Box>
    );
}
